import { Node } from "#/decision_tree_viewer/models/node";

import { useVisitTypeStore } from "#/decision_tree_viewer/store/visit-type";

import { isEmpty } from "lodash";
import { stripHtml } from "#/shared/utils/string";

export class SelectVisitTypeNode extends Node {
  get defaults() {
    return {
      ...super.defaults,
      name: "SelectVisitTypeNode",

      question: undefined,

      visitTypeIds: [],

      selectedVisitTypeId: undefined,
      targetNodeId: "continue",

      switchDecisionTree: false,
      disableAutoSelect: false,
    };
  }

  get currentState() {
    return { selectedVisitTypeId: this.selectedVisitTypeId };
  }

  get questionText() {
    return this.question || "";
  }

  get analyticsSummary() {
    if (!this.selectedVisitTypeId) {
      return null;
    }
    const { visitTypes } = useVisitTypeStore();
    const visitType = visitTypes.find((v) => v.id === this.selectedVisitTypeId);
    return {
      nodeType: this.name,
      nodeId: this.id,
      questionText: stripHtml(this.questionText).trim(),
      selectedAnswer: visitType?.name,
      selectedAnswerId: this.selectedVisitTypeId,
    };
  }

  get context() {
    return { visitTypeId: this.selectedVisitTypeId, selectVisitTypeNodeShown: true };
  }

  get possibleNextNodeIds() {
    return [this.targetNodeId, "missing-visit-type"];
  }

  filteredVisitTypes(context) {
    const visibilityFilter = (visitType) => visitType.visible;
    const patientTypeFilter = (visitType) => visitType.validFor(context.patientType);

    const nodeLimitFilter = (visitType) => {
      if (isEmpty(this.visitTypeIds)) {
        return true;
      }

      return this.visitTypeIds.includes(visitType.id);
    };

    const providerLimitFilter = (visitType) => {
      if (isEmpty(context.providerVisitTypeIds)) {
        return true;
      }

      return context.providerVisitTypeIds.includes(visitType.id);
    };

    const { visitTypes } = useVisitTypeStore();
    const limitedVisitTypes = visitTypes
      .filter(visibilityFilter)
      .filter(patientTypeFilter)
      .filter(nodeLimitFilter)
      .filter(providerLimitFilter);

    const matchingVisitType = this.matchingProviderVisitType(
      context.visitType,
      context.patientType,
      context.providerVisitTypeIds,
    );

    if (matchingVisitType && !limitedVisitTypes.includes(matchingVisitType)) {
      limitedVisitTypes.push(matchingVisitType);
    }

    return limitedVisitTypes;
  }

  beforeShow(context) {
    if (context.selectVisitTypeNodeShown && context.visitTypeId) {
      // If we've already selected a visit type, don't change it
      this.selectedVisitTypeId = context.visitTypeId;
    } else if (!context.patientType) {
      // If there's no patient type, we can't filter visit types, so just use the current visit type
      this.selectedVisitTypeId = context.visitTypeId;
    } else if (this.disableAutoSelect) {
      this.selectedVisitTypeId = undefined;
    } else if (this.hasSingleFilteredVisitType(context)) {
      // If there's only one valid visit type, auto-select it
      this.selectedVisitTypeId = this.singleFilteredVisitType(context).id;
    } else {
      this.selectedVisitTypeId = this.matchingProviderVisitType(
        context.visitType,
        context.patientType,
        context.providerVisitTypeIds,
      )?.id;
    }
  }

  autoAdvance(context) {
    return this.shouldSkip(context);
  }

  nextNodeId(context) {
    if (this.hasNoValidVisitTypes(context)) {
      return "missing-visit-type";
    }

    const nodeId = this.determineDecisionTreeSwitch(this.selectedVisitTypeId, context).nodeId;

    if (this.shouldSkip(context)) {
      return nodeId;
    }

    if (this.selectedVisitTypeId) {
      return nodeId;
    }
  }

  reset() {
    this.selectedVisitTypeId = undefined;
  }

  isDecisionTreeSwitch(context) {
    return this.determineDecisionTreeSwitch(this.selectedVisitTypeId, context).isSwitch;
  }

  // PRIVATE

  shouldSkip(context) {
    // If we've already shown this node and have a visit type, don't show it again
    if (context.selectVisitTypeNodeShown && context.visitTypeId) {
      return true;
    }

    // If there's no patient type, we can't filter visit types, so skip the node
    if (!context.patientType) {
      return true;
    }

    if (this.hasNoValidVisitTypes(context)) {
      return true;
    }

    if (this.disableAutoSelect) {
      return false;
    }

    // If there's only one valid visit type to choose from, skip the node
    return this.hasSingleFilteredVisitType(context);
  }

  hasNoValidVisitTypes(context) {
    return this.filteredVisitTypes(context).length === 0;
  }

  hasSingleFilteredVisitType(context) {
    return this.singleFilteredVisitType(context) !== undefined;
  }

  singleFilteredVisitType(context) {
    const filteredVisitTypes = this.filteredVisitTypes(context);
    return filteredVisitTypes.length === 1 ? filteredVisitTypes[0] : undefined;
  }

  matchingProviderVisitType(visitType, patientType, providerVisitTypeIds) {
    if (!visitType || !patientType || !providerVisitTypeIds) {
      return undefined;
    }

    const { visitTypes } = useVisitTypeStore();

    // Start by finding all valid visit types that match the current visit type's name
    const matchingValidVisitTypes = visitTypes.filter(
      (v) => v.matchName === visitType.matchName && v.validFor(patientType),
    );

    // Option 1: Find a matching valid visit type that the provider has
    const matchingProviderVisitType = matchingValidVisitTypes.find((v) => providerVisitTypeIds.includes(v.id));

    // Option 2: Find a matching valid visit type that has the same specialty restrictions as the current visit type
    const matchingSpecialtyRestrictionVisitType = matchingValidVisitTypes.find((v) =>
      v.hasMatchingSpecialtyRestrictions(visitType),
    );

    // Option 3: Find a matching valid visit type that has no specialty restrictions
    const matchingNoSpecialtyRestrictionVisitType = matchingValidVisitTypes.find((v) => v.hasNoSpecialtyRestrictions());

    // Option 4: Find a valid visit type that has the same category as the current visit type
    // It's likely that the user has only picked the category of the visit type (e.g. video or in-person), so this is a reasonable fallback
    const validMatchingCategoryVisitType = visitTypes.find(
      (v) => v.validFor(patientType) && providerVisitTypeIds.includes(v.id) && v.category === visitType.category,
    );

    // Option 5: If we can't find a matching visit type, pre-select the current visit type even though it isn't valid for this patientType
    // We'll show an error later if the user doesn't change the visit type
    let originalVisitType = undefined;
    if (!this.disableAutoSelect) {
      originalVisitType = visitType;
    }

    return (
      matchingProviderVisitType ??
      matchingSpecialtyRestrictionVisitType ??
      matchingNoSpecialtyRestrictionVisitType ??
      validMatchingCategoryVisitType ??
      originalVisitType
    );
  }
}
